import { isObject } from 'ionic-angular/util/util';
import * as moment from 'moment';
import { Field } from '../field/field.model';
import { File } from '../file/file.model';
import { Report } from '../report/report.model';
import {
    IRestrictedAccountObjectArgs,
    RestrictedAccountObject
} from '../restricted-account-object/restricted-account-object.model';
import { VersionedObject } from '../versioned-object';

export enum JobPointerEnum {
    NEXT = 1,
    PREVIOUS = 2,
    FIELD = 3
}

export interface IJobPointer {
    account: string;
    copyReason?: string;
    createdBy?: string;
    id: string;
    job: string;
    modifiedBy?: string;
    reference: string;
    serverCreated?: string;
    serverModified?: string;
    targetJob: string;
}

export const isJob = (object): object is Job => {
    return object.id && object.reference;
};

export interface IJobArgs extends IRestrictedAccountObjectArgs {
    reference?: string;
    jobDate?: string;
    jobType?: string;
    billable?: string;
    fields?: Array<Field>;
    files?: Array<string> | Array<File>;
    reports?: Array<string> | Array<Report>;
    notes?: Array<string>;
    jobStatus?: string;
    version?: number;
    assigneeName?: string;
    assigneeId?: string;
    timeZoneOffset?: number;
    pendingFields?: number;
    previous?: {
        id: string; // the `id` of the job that this job was cloned from
        reference: string; // the `reference` of the job that this job was cloned from
    };
    next?: Array<{
        id: string; // the `id` of a job that was cloned from this job
        reference: string; // the `reference` of a job that was cloned from this job
    }>;
    dirty?: boolean;
}

const defaults = () => ({
    reference: '',
    jobDate: undefined,
    jobType: undefined,
    billable: '',
    fields: [],
    files: [],
    reports: [],
    notes: [],
    jobStatus: undefined,
    version: 0,
    assigneeName: '',
    assigneeId: '',
    timeZoneOffset: moment().utcOffset(),
    pendingFields: 0,
    previous: undefined,
    next: [],
    dirty: false
} as IJobArgs);

export class Job extends RestrictedAccountObject implements IJobArgs, VersionedObject {
    reference: string;
    jobDate: string;
    jobType: string;
    billable: string;
    fields: Array<Field>;
    files: Array<string> | Array<File>;
    reports: Array<string>;
    notes: Array<string>;
    jobStatus: string;
    version: number;
    assigneeName: string;
    assigneeId: string;
    timeZoneOffset: number;
    pendingFields: number;
    previous: IJobPointer;
    next: Array<IJobPointer>;
    dirty: boolean;

    constructor(data?: IJobArgs) {
        super({...defaults(), ...data} as any);
    }

    get fieldIds(): Array<string> {
        // @todo fields
        return (this.fields as Array<any>).map(value => {
            if (isObject(value)) {
                return value.id;
            } else {
                return value;
            }
        });
    }

    get reportIds(): Array<string> {
        return (this.reports as Array<any>).map(value => {
            if (isObject(value)) {
                return value.id;
            } else {
                return value;
            }
        });
    }

    get fileIds(): Array<string> {
        return (this.files as Array<any>).map(value => {
            if (isObject(value)) {
                return value.id;
            } else {
                return value;
            }
        });
    }

    get nextIds(): Array<string> {
        return this.next.map(next => next.id);
    }

    get previousId(): string {
        return this.previous && this.previous.id;
    }

    isFresherThan(job: Job): boolean {

        if (this.compareLists(this.fieldIds, job.fieldIds) === false) {
            return false;
        }

        if (this.compareLists(this.reportIds, job.reportIds) === false) {
            return false;
        }

        if (this.compareLists(this.fileIds, job.fileIds) === false) {
            return false;
        }

        if (this.compareLists(this.nextIds, job.nextIds) === false) {
            return false;
        }

        if (this.previousId !== job.previousId) {
            return false;
        }

        return true;
    }

    protected compareLists(a: Array<string>, b: Array<string>): boolean {
        const allAInB = !a.find(aid => !b.find(bid => aid === bid));
        const allBInA = !b.find(bid => !a.find(aid => bid === aid));
        return allAInB && allBInA;
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/job/job.model.ts